import React from 'react';
import classNames from 'classnames';

import { FCWithChildren } from '@types';
import Button from '../Button';
import useLayoutContext from '@hooks/useLayoutContext';

type ActionSectionProps = {
  className?: string;
  id?: string;
};

type ActionSectionSectionProps = {
  className?: string;
  icon?: React.ReactNode;
};

type ActionSectionLinkProps = React.ComponentProps<typeof Button> & {
  className?: string;
};

const ActionSectionHeader: FCWithChildren<ActionSectionSectionProps> = ({
  children,
  className,
  icon,
}) => {
  return (
    <div
      className={classNames('w-full p-5 text-white', className)}
      style={{
        textShadow:
          '-3px 0 0 rgba(61, 71, 85, 0.55), 3px 0 0 rgba(61, 71, 85, 0.55), 0 -3px 0 rgba(61, 71, 85, 0.55), 0 3px 0 rgba(61, 71, 85, 0.55)',
      }}
    >
      <div className="flex items-end gap-3">
        {icon ? <span className="flex-shrink-0">{icon}</span> : null}
        <span className="min-w-0">{children}</span>
      </div>
    </div>
  );
};

const ActionSectionBody: FCWithChildren<ActionSectionSectionProps> = ({ children, className }) => {
  return (
    <div
      className={classNames(
        'w-full flex-1 flex flex-col bg-white rounded-b-[16px] border px-5 border-black',
        className,
      )}
    >
      {children}
    </div>
  );
};

const ActionSectionLinks: FCWithChildren<ActionSectionSectionProps> = ({ children, className }) => {
  return <div className={classNames('w-full', className)}>{children}</div>;
};

const ActionSectionLink: React.FC<ActionSectionLinkProps> = ({ children, className, ...props }) => {
  return (
    <Button
      layout="link"
      size="noPadding"
      className={classNames(
        'action-section-link block !py-1 !px-6 !border-t border-t-black dark:border-t-gray-650 -mx-5 justify-start text-left text-[14px] font-medium leading-[30px] tracking-[-0.02em] text-black',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
};

type ActionSectionComponent = FCWithChildren<ActionSectionProps> & {
  Header: typeof ActionSectionHeader;
  Body: typeof ActionSectionBody;
  Links: typeof ActionSectionLinks;
  Link: typeof ActionSectionLink;
};

const GRADIENT_STYLES: Record<string, string> = {
  '1': 'linear-gradient(-10deg, var(--background-secondary) 7.46%, var(--background-secondary) 3.79%, var(--background-secondary) 38.59%, var(--background-primary) 81.73%, var(--background-primary) 100%)',
};

const ActionSection: ActionSectionComponent = ({ children, className, id }) => {
  const { gradientStyle } = useLayoutContext();

  const background =
    gradientStyle && gradientStyle !== 'none' && GRADIENT_STYLES[gradientStyle]
      ? GRADIENT_STYLES[gradientStyle]
      : 'var(--background-primary)';

  return (
    <section
      id={id}
      className={classNames(
        'flex flex-col rounded-2xl py-1 rounded-[16px] shadow-[5px_8px_22px_rgba(0,0,0,0.25)] md:min-w-[300px] min-h-[300px] max-w-full',
        className,
      )}
      style={{ background }}
    >
      {children}
    </section>
  );
};

ActionSection.Header = ActionSectionHeader;
ActionSection.Body = ActionSectionBody;
ActionSection.Links = ActionSectionLinks;
ActionSection.Link = ActionSectionLink;

export default ActionSection;
